import { __decorate, __param } from 'tslib';
import * as i0 from '@angular/core';
import { Optional, Injectable, Directive } from '@angular/core';
import { timeout, tap, first, catchError, map, mergeMap, distinctUntilChanged } from 'rxjs/operators';
import { from, throwError, Observable, of, isObservable, BehaviorSubject } from 'rxjs';
import * as i1 from '@okta/okta-auth-js';
import { OktaAuth } from '@okta/okta-auth-js';
export { _isNumberValue, coerceArray, coerceBooleanProperty, coerceCssPixelValue, coerceElement, coerceNumberProperty, coerceStringArray } from '@angular/cdk/coercion';

const interception = (request, next, config, oktaAuth, accessToken = '', 
/* csrf = '', */
additionalHeaders = {}, interceptorChain = tap(x => x), includeLogic = true) => {
    if (!accessToken) {
        // try to get token from storage
        accessToken = oktaAuth.getAccessToken();
    }
    const timeoutValue = +(request.headers.get('timeout') || config.defaultTimeout || 30000);
    const requestUrl = request.url.split('?')[0];
    const regexUrls = (config.authedUrls || []).map(x => new RegExp(x));
    if ((includeLogic && regexUrls.find(x => x.test(requestUrl))) ||
        (!includeLogic && !regexUrls.find(x => x.test(requestUrl)))) {
        if (accessToken) {
            // construct header
            let headers = {
                /* eslint-disable */
                authorization: 'Bearer ' + accessToken,
                'content-type': 'application/json',
                /* eslint-enable */
                ...additionalHeaders,
                ...(request.headers.keys().reduce((bag, key) => ({ ...bag, [key]: request.headers.get(key) }), {}))
            };
            // fix for multipart/form-data
            if (request.headers.has('content-type') && request.headers.get('content-type') === 'multipart/form-data') {
                request = request.clone({ headers: request.headers.delete('content-type') });
                headers = Object.keys(headers).filter(key => key !== 'content-type').reduce((bag, key) => ({ ...bag, [key]: headers[key] }), {});
            }
            // validate header
            Object.keys(headers).filter(key => !headers[key]).forEach(key => console.warn('interceptor::' + key + '::must have a value, current value is ' + headers[key]));
            // clone request with new headers
            request = request.clone({
                setHeaders: headers,
                withCredentials: true
            });
        }
        else {
            console.warn('Could not find a token for a request to ' + requestUrl, 'here is your config ' + JSON.stringify(config));
        }
    }
    return next.handle(request).pipe(timeout(timeoutValue), interceptorChain);
};
/* eslint-disable max-len */
/**
 * Authorization Interceptor
 *
 * @description This version of auth interceptor uses include logic. Put your urls that you would like to add a token to in the authUrls. You should not have to extend this as it has all the logic to work out of the box, though it is implementable.
 * @param {BasicAuthConfig} config - the current config
 * @param {OperatorFunction} chain - (optional) allows the observable chain for intercepting to be extended for the entire application
 * @example
 * // normal usage
 * providers: [
 * ...
 * {
 *    provide: HTTP_INTERCEPTORS,
 *    useClass: AuthInterceptor,
 *    deps: [BasicAuthConfig],
 *    multi: true
 *  },
 *  ...
 * ]
 * @example
 * // advanced usage
 * providers: [
 * ...
 * {
 *    provide: HTTP_INTERCEPTORS,
 *    useFactory: (config: BasicAuthConfig) => new AuthInterceptor(
 *      config,
 *      catchError(e => {
 *        if(e.statusCode === 401) {
 *          // do log out
 *        }
 *        return throwError(e);
 *      }
 *    )),
 *    deps: [BasicAuthConfig],
 *    multi: true
 *  },
 *  ...
 * ]
 * @implements {HttpInterceptor}
 */
let AuthInterceptor = class AuthInterceptor {
    constructor(config, oktaAuth, chain) {
        this.config = config;
        this.oktaAuth = oktaAuth;
        this.chain = chain;
    }
    intercept(request, next, accessToken, additionalHeaders) {
        return interception(request, next, this.config, this.oktaAuth, accessToken, additionalHeaders, this.chain);
    }
};
AuthInterceptor = __decorate([
    __param(2, Optional())
], AuthInterceptor);
/**
 * Authorization Exclusion Interceptor
 *
 * @description This version of auth interceptor uses exclude logic. Put your urls that you would NOT like to add a token to in the authUrls. You should not have to extend this as it has all the logic to work out of the box, though it is implementable.
 * @param {BasicAuthConfig} config - the current config
 * @param {OperatorFunction} chain - (optional) allows the observable chain for intercepting to be extended for the entire application
 * @example
 * // normal usage
 * providers: [
 * ...
 * {
 *    provide: HTTP_INTERCEPTORS,
 *    useClass: AuthExclusionInterceptor,
 *    deps: [BasicAuthConfig],
 *    multi: true
 *  },
 *  ...
 * ]
 * @example
 * // advanced usage
 * providers: [
 * ...
 * {
 *    provide: HTTP_INTERCEPTORS,
 *    useFactory: (config: BasicAuthConfig) => new AuthExclusionInterceptor(
 *      config,
 *      catchError(e => {
 *        if(e.statusCode === 401) {
 *          // do log out
 *        }
 *        return throwError(e);
 *      }
 *    )),
 *    deps: [BasicAuthConfig],
 *    multi: true
 *  },
 *  ...
 * ]
 * @implements {HttpInterceptor}
 */
let AuthExclusionInterceptor = class AuthExclusionInterceptor {
    constructor(config, oktaAuth, chain) {
        this.config = config;
        this.oktaAuth = oktaAuth;
        this.chain = chain;
    }
    intercept(request, next, accessToken, additionalHeaders) {
        return interception(request, next, this.config, this.oktaAuth, accessToken, additionalHeaders, this.chain, false);
    }
};
AuthExclusionInterceptor = __decorate([
    __param(2, Optional())
], AuthExclusionInterceptor);

class BasicAuthConfig {
    constructor() {
        /* app info */
        this.appName = undefined;
        this.region = undefined;
        /* api settings */
        this.authedUrls = undefined;
        this.defaultTimeout = undefined;
        this.urls = {};
        /* auth settings */
        this.clientId = undefined;
        this.issuer = undefined;
        this.redirectUri = undefined;
        this.scopes = undefined;
        this.guardRedirectUri = undefined;
        this.maxClockSkew = undefined;
        /* environmental overrides */
        this.overrides = undefined;
    }
}

/* eslint-disable no-unused-vars */
class CommonAuthGuard {
    constructor(oktaAuth, config) {
        this.oktaAuth = oktaAuth;
        this.config = config;
        this.guard$ = from(this.oktaAuth.isAuthenticated());
    }
    canActivate(route, state) {
        return this.guard$.pipe(first(), 
        // this should redirect back to a route that has no guard, maybe like the login route or href back to RS
        tap(allowed => { if (!allowed) {
            window.location.href = !this.config.guardRedirectUri ? '/login' : this.config.guardRedirectUri;
        } }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CommonAuthGuard, deps: [{ token: i1.OktaAuth }, { token: BasicAuthConfig }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CommonAuthGuard, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CommonAuthGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.OktaAuth }, { type: BasicAuthConfig }] });

class CommonConfig {
    constructor() {
        this.footer = { links: [], logo: undefined };
        this.header = { logo: undefined, helpIcon: undefined };
        this.signIn = { imgSrc: undefined };
        this.loader = { imgSrc: undefined };
    }
}
class RooseveltCommonConfig {
    constructor() {
        this.footer = { links: [{ text: 'Help', target: '_blank', url: 'https://help.my.rooseveltsolutions.com/' }], logo: undefined };
        this.header = {
            logo: { imgSrc: '/assets/images/roosevelt.svg', target: '_self', url: '/', text: 'Roosevelt Logo' },
            helpIcon: { imgSrc: 'support', target: '_blank', url: 'https://help.my.rooseveltsolutions.com/', text: 'help icon' }
        };
        this.signIn = { imgSrc: '/assets/images/roosevelt.svg' };
        this.loader = { imgSrc: '/assets/images/roosevelt-loader.gif' };
    }
}

/* eslint-disable no-unused-vars */
class CommonUserService {
    get claims() {
        return this.getOktaClaims();
    }
    constructor(oktaAuth, config) {
        this.oktaAuth = oktaAuth;
        this.config = config;
    }
    getFullName() { return [this.claims.firstName, this.claims.lastName].filter(x => !!x).join(' '); }
    getOktaClaims() { return this.oktaAuth.tokenManager.getTokensSync().idToken?.claims; }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CommonUserService, deps: [{ token: i1.OktaAuth }, { token: BasicAuthConfig }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CommonUserService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CommonUserService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.OktaAuth }, { type: BasicAuthConfig }] });

let ErrorInterceptor = class ErrorInterceptor {
    constructor(config, oktaAuth, errorConfig) {
        this.config = config;
        this.oktaAuth = oktaAuth;
        this.errorConfig = errorConfig;
    }
    intercept(request, next) {
        return next.handle(request).pipe(catchError(e => {
            if (!this.errorConfig && e.status === 401) {
                handle401(this.config, this.oktaAuth);
            }
            else if (this.errorConfig) {
                if (Object.keys(this.errorConfig).includes(e.status?.toString())) {
                    this.errorConfig[e.status](this.config, this.oktaAuth, e);
                }
            }
            return throwError(e);
        }));
    }
};
ErrorInterceptor = __decorate([
    __param(2, Optional())
], ErrorInterceptor);
const handle401 = (config, oktaAuth) => {
    // needed so other apps know to log out
    localStorage.setItem('logout-catalyst', config.appName);
    localStorage.setItem('logout-catalyst', '');
    sessionStorage.clear();
    localStorage.clear();
    oktaAuth.signOut();
};

class ErrorMessageMap {
    constructor() {
        // use the default error message map
        if (!Object.keys(this).length) {
            Object.keys(defaultErrorMessageMap).forEach(key => this[key] = defaultErrorMessageMap[key]);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ErrorMessageMap, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ErrorMessageMap, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ErrorMessageMap, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [] });
const defaultErrorMessageMap = {
    required: () => 'This field is required',
    isBlankSpace: () => 'Blank spaces not allowed',
    minlength: ({ requiredLength }) => `Value must be at least ${requiredLength} characters in length`,
    maxlength: ({ requiredLength }) => `Value must be at most ${requiredLength} characters in length`,
    incorrectDateFormat: format => `Date must follow the ${format} format`,
    futureDate: () => 'Date must not be in the future',
    currentDate: () => 'Date must not be a current date',
    email: () => 'Invalid email',
    zipCode: () => 'Invalid zipCode',
    invalidPhone: () => 'Invalid phone number',
    validSqa: () => 'Security Answer should contain 3-15 characters, letters, numbers, no special characters and should not start with a number',
    invalidSsn: () => 'Invalid ssn number',
    alphaNumeric: () => 'Value must be numbers or characters',
    alpha: () => 'Invalid characters, special characters and numbers not allowed',
    password: () => 'Password should be atleast 8 characters long and should contain'
        + ' at least one of each - numeric, upper case, lower case and special characters',
    mismatch: () => `Passwords don't match`,
    duplicateSsn: () => 'Duplicate SSN',
    usernameExists: () => `Username already in use. Please select another username`,
    pattern: () => 'Invalid input',
    invalidDate: () => 'Invalid date format',
    endDateValid: () => 'End Date must be on or after Start Date',
    min: ({ min }) => `Must be a minimum of ${min}`,
    max: ({ max }) => `Should not exceed a maximum of ${max}`
};

/* eslint-disable max-len */
class Lux {
    constructor() {
        this.state = {};
        this.listeners = [];
        this.stateObs = [];
        this.state$ = new Observable(obs => { this.stateObs = [...this.stateObs, obs]; });
        /**
         * returns get and set with the same params and returns
         *
         * @param stateKey a function that returns a first level key of the state
         */
        this.byKey = (stateKey) => ({
            /**
             * updates a fraction of state
             *
             * @param selector a function which locates a parent object you are interested in updating
             * @param payload a partial value of the selected item or an observable of an item or a function to produce an item using the selected state
             */
            set: (selector, payload) => {
                this.set(state => selector(state[stateKey()]), payload);
            },
            /**
             * returns an observable for updates to the selected state
             *
             * @param selector used to select a location in state
             * @param key use a key to tie a group of gets together, this helps debugging and destorying
             */
            get: (selector, key = 'lux-default') => of('').pipe(map(stateKey), mergeMap(sKey => this.get(state => selector(state[sKey]), key)))
        });
    }
    /**
     * this is at the highest level and will emit anytime something changes.
     */
    allChanges() {
        return this.state$;
    }
    /**
     * register takes a piece of the state and adds it to the existing.
     * it works based on the default state you pass in. if the default is not complete the state will fail.
     * states can be registered down to the component level. best use-case if for module level registration.
     *
     * @param stateKey a key from the first level of state
     * @param defaultState a structure that matches the fractal state being registered
     */
    register(stateKey, defaultState) {
        // regisiter the default fraction of the state
        this.state = {
            ...this.state,
            // deep clone to avoid contaminating the initial state
            [stateKey]: this.deepCopy(defaultState)
        };
        // trigger the state observers to notify. this covers the empty start condition
        this.stateObs.forEach(obs => obs.next(this.state));
    }
    /**
     * updates a fraction of state
     *
     * @param selector a function which locates a parent object you are interested in updating
     * @param payload a partial value of the selected item or an observable of an item or a function to produce an item using the selected state
     */
    set(selector, payload) {
        if (typeof payload === 'function') {
            // get the value from state and pass it into a provided funtion which returns an object to set
            const selectorVal = selector(this.state);
            this.set(selector, payload(selectorVal));
        }
        else {
            // use the selector to return the underlying data
            // selectors cannot have modifiers, they must be pure
            // if the payload is not an observable, convert it to one
            const obs$ = !isObservable(payload) ? of(payload) : payload;
            obs$
                .pipe(first(), 
            // break all references to object
            map(this.deepCopy))
                .subscribe(val => {
                const selectorVal = selector(this.state);
                // replace all of the key you find in the payload
                Object.assign(selectorVal, val);
                // Object.keys(val).forEach(key => selectorVal[key] = val[key]);
                // remove any listeners that are closed or have no subscriptions
                this.listeners = this.listeners.filter(x => !(x.observable.closed));
                this.listeners.forEach(x => x.observable.next(x.selector(this.state)));
                this.stateObs.forEach(obs => obs.next(this.state));
            });
        }
    }
    /**
     * returns an observable for updates to the selected state
     *
     * @param selector used to select a location in state
     * @param key use a key to tie a group of gets together, this helps debugging and destorying
     */
    get(selector, key = 'lux-default') {
        const listener = { selector, key, observable: new BehaviorSubject(null) };
        this.listeners = [...this.listeners, listener];
        // all observables have a map and distictUntilChanged pipe
        return listener.observable.asObservable().pipe(
        // the default for this behavior subject is null, meaning when subscribed to for the first time it could emit null.
        // since most likely there is a value at the time of subscription we just need to run the selector function against our state.
        // this will allow us to set up our observables up ahead of the state being ready
        // please note, as it is not normally customary, assigning null to a value in the state will force the selector function to run again
        map(value => JSON.stringify(value !== null ? value : this.selectorObject(listener.selector))), distinctUntilChanged(), map(value => value ? JSON.parse(value) : value));
    }
    /**
     * unsubscribe from all(a group of) observables with the same key.
     * it is important that you scope your keys if keys across the application use the same name they will be unsubscribed to, causing issues.
     *
     * @example
     *
     * export class ExampleComponent implements OnDestroy {
     *  luxId = 'myapp-' + this.constructor.name;
     *  list$: Observable<IItems[]> = this.lux.get(getItems, this.luxId);
     *  search$: Observable<ISearch> = this.lux.get(getSearch, this.luxId);
     *
     *  ngOnDestroy() {
     *    this.lux.destory(this.luxId);
     *  }
     * }
     *
     * @param key a string used to describe the group
     */
    destroy(key) {
        if (key) {
            this.listeners.filter(x => x.key === key && x.observable && !x.observable.closed).forEach(x => x.observable.unsubscribe());
            this.listeners = this.listeners.filter(x => !(x.observable.closed));
        }
    }
    selectorObject(selector) {
        const val = selector(this.state);
        if (this.isObject(val)) {
            return { ...val };
        }
        else {
            return val;
        }
    }
    isObject(obj) {
        const type = typeof obj;
        return type === 'function' || type === 'object' && !!obj && !Array.isArray(obj);
    }
    deepCopy(val) {
        return val ? JSON.parse(JSON.stringify(val)) : val;
    }
}

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/dot-notation */
/* eslint-disable @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match */
class LuxDevTools extends Lux {
    constructor() {
        super();
        this._changeLog = [];
        this._obsCount = [];
        this._obsData = [];
        this._startTime = new Date().valueOf();
        document.body.addEventListener('click', () => this.createGraphSet());
        // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
        (function () {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = 'https://cdn.jsdelivr.net/npm/chart.js@2.8.0';
            document.head.appendChild(script);
            document.body.insertAdjacentHTML('beforeend', `<canvas id="myChart" style="display: none;" width="400" height="400"></canvas>`);
        })();
        window['lux'] = {
            changes: {
                theLast: (amount) => this._changeLog.slice(-1 * amount),
                all: () => { console.table(this._changeLog); }
            },
            observables: {
                generateGraph: () => {
                    const ctx = document.getElementById('myChart');
                    ctx.setAttribute('width', '400');
                    ctx.setAttribute('height', '400');
                    new Chart(ctx, {
                        type: 'line',
                        data: {
                            labels: this._obsData.map(x => ((x.time - this._startTime) / 1000).toFixed(2) + 's'),
                            datasets: [{
                                    label: 'New',
                                    data: this._obsData.reduce((bag, data) => ([...bag, data.newGetCount]), []),
                                    backgroundColor: 'rgba(255, 99, 132, 0.2)',
                                    borderWidth: 1
                                },
                                {
                                    label: 'Existing',
                                    data: this._obsData.reduce((bag, data) => ([...bag, data.existingGetCount]), []),
                                    backgroundColor: 'rgba(54, 162, 235, 0.2)',
                                    borderWidth: 1
                                },
                                {
                                    label: 'Collected',
                                    data: this._obsData.reduce((bag, data) => ([...bag, data.disposedGetCount]), []),
                                    backgroundColor: 'rgba(255, 206, 86, 0.2)',
                                    borderWidth: 1
                                }]
                        },
                        options: {
                            responsive: false,
                            scales: {
                                yAxes: [{
                                        ticks: {
                                            beginAtZero: true
                                        }
                                    }]
                            }
                        }
                    });
                    setTimeout(() => {
                        console.log('%c ', '' +
                            'font-size: 0;' +
                            'padding-left: 400px;' +
                            'padding-bottom: 400px;' +
                            'background: url("' + ctx.toDataURL() + '");' +
                            'background-size: 400px 400px;' +
                            '');
                    }, 2000);
                    return '...generating chart...';
                }
            }
        };
    }
    set(selector, payload) {
        if (typeof payload === 'function') {
            // get the value from state and pass it into a provided funtion which returns an object to set
            const selectorVal = selector(this.state);
            this.set(selector, payload(selectorVal));
        }
        else {
            // use the selector to return the underlying data
            // selectors cannot have modifiers, they must be pure
            // if the payload is not an observable, convert it to one
            const obs$ = !isObservable(payload) ? of(payload) : payload;
            obs$.pipe(first()).subscribe(val => {
                const selectorVal = selector(this.state);
                const oldVal = Object.keys(val).reduce((bag, key) => ({ ...bag, [key]: selectorVal[key] }), {});
                const changedKeys = Object.keys(val).filter(key => JSON.stringify(val[key]) !== JSON.stringify(oldVal[key]));
                this._changeLog = [...this._changeLog, {
                        selector: selector.toString().indexOf('return') !== -1 ?
                            'state=>state.' + selector.toString().split('return')[1].replace(';', '').replace('}', '').trim().split('.').slice(1).join('.') :
                            selector.toString(),
                        oldVal: JSON.stringify(oldVal),
                        newVal: changedKeys.length ? JSON.stringify(changedKeys.reduce((bag, key) => ({ ...bag, [key]: val[key] }), {})) : 'No Changes'
                    }];
                // replace all of the key you find in the payload
                Object.keys(val).forEach(key => selectorVal[key] = val[key]);
                // remove any listeners that are closed or have no subscriptions
                this.listeners = this.listeners.filter(x => !(x.observable.closed));
                this.listeners.forEach(x => x.observable.next(this.selectorObject(x.selector)));
                this.stateObs.forEach(obs => obs.next(this.state));
            });
        }
    }
    get(selector, key = 'lux-default') {
        const listener = { id: this.getUId(), selector, key, observable: new BehaviorSubject(this.selectorObject(selector)) };
        this.listeners = [...this.listeners, listener];
        // all observables have a distictUntilChanged pipe
        return listener.observable.pipe(distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)));
    }
    getUId() {
        const crypto = window.crypto || window['msCrypto'];
        return crypto.getRandomValues(new Uint32Array(1))[0].toString();
    }
    createGraphSet() {
        const lastIdSet = this._obsCount.length ? this._obsCount[this._obsCount.length - 1] : [];
        const allIdSet = this.listeners.map((l) => l['id']);
        this._obsData = [...this._obsData, {
                time: new Date().valueOf(),
                disposedGetCount: lastIdSet.filter(x => allIdSet.indexOf(x) === -1).length,
                newGetCount: allIdSet.filter(x => lastIdSet.indexOf(x) === -1).length,
                existingGetCount: lastIdSet.filter(x => allIdSet.indexOf(x) !== -1).length
            }];
        this._obsCount = [...this._obsCount, allIdSet];
    }
}

/* eslint-disable max-len */
/**
 * Lux High Performant
 *
 * @description !THIS IS EXPERIMENTAL! This version of Lux changes the setters so that all the objects can be frozen.
 * This allows us to compare objects directly to tell if the reference has changed.
 */
class LuxHP {
    constructor() {
        /**
         * updates a fraction of state
         *
         * @param payload a partial value of the selected item or an observable of an item or a function to produce an item using the selected state
         * @example lux.set.<my_state_key>(state => ({...state, [a]: {...state.a, b: 2}}))
         */
        this.set = {};
        this.state = {};
        this.listeners = [];
        this.stateObs = [];
        this.state$ = new Observable(obs => { this.stateObs = [...this.stateObs, obs]; });
    }
    /**
     * this is at the highest level and will emit anytime something changes.
     */
    allChanges() {
        return this.state$;
    }
    /**
     * register takes a piece of the state and adds it to the existing.
     * it works based on the default state you pass in. if the default is not complete the state will fail.
     * states can be registered down to the component level. best use-case if for module level registration.
     *
     * @param stateKey a key from the first level of state
     * @param defaultState a structure that matches the fractal state being registered
     */
    register(stateKey, defaultState) {
        // regisiter the default fraction of the state
        this.state = {
            ...this.state,
            // deep clone to avoid contaminating the initial state
            [stateKey]: Object.freeze(defaultState)
        };
        this.set = { ...this.set, [stateKey]: this.setFn(stateKey) };
        // trigger the state observers to notify. this covers the empty start condition
        this.stateObs.forEach(obs => obs.next(this.state));
    }
    /**
     * returns an observable for updates to the selected state
     *
     * @param selector used to select a location in state
     * @param key use a key to tie a group of gets together, this helps debugging and destorying
     */
    get(selector, key = 'lux-default') {
        const listener = { selector, key, observable: new BehaviorSubject(selector(this.state)) };
        this.listeners = [...this.listeners, listener];
        // all observables have a map and distictUntilChanged pipe
        return listener.observable.asObservable().pipe(
        // the default for this behavior subject is null, meaning when subscribed to for the first time it could emit null.
        // since most likely there is a value at the time of subscription we just need to run the selector function against our state.
        // this will allow us to set up our observables up ahead of the state being ready
        // please note, as it is not normally customary, assigning null to a value in the state will force the selector function to run again
        distinctUntilChanged(), map(value => Object.freeze(value)));
    }
    /**
     * unsubscribe from all(a group of) observables with the same key.
     * it is important that you scope your keys if keys across the application use the same name they will be unsubscribed to, causing issues.
     *
     * @example
     *
     * export class ExampleComponent implements OnDestroy {
     *  luxId = getUid();
     *  list$: Observable<IItems[]> = this.lux.get(getItems, this.luxId);
     *  search$: Observable<ISearch> = this.lux.get(getSearch, this.luxId);
     *
     *  ngOnDestroy() {
     *    this.lux.destory(this.luxId);
     *  }
     * }
     *
     * @param key a string used to describe the group
     */
    destroy(key) {
        if (key) {
            this.listeners.filter(x => x.key === key && x.observable && !x.observable.closed).forEach(x => x.observable.unsubscribe());
            this.listeners = this.listeners.filter(x => !(x.observable.closed));
        }
    }
    setFn(stateKey) {
        return (payload) => {
            if (typeof payload === 'function') {
                // get the value from state and pass it into a provided funtion which returns an object to set
                this.setFn(stateKey)(payload(this.state[stateKey]));
            }
            else {
                // use the selector to return the underlying data
                // selectors cannot have modifiers, they must be pure
                // if the payload is not an observable, convert it to one
                const obs$ = !isObservable(payload) ? of(payload) : payload;
                obs$
                    .pipe(first())
                    .subscribe(val => {
                    // replace all of the key you find in the payload
                    this.state = { ...this.state, [stateKey]: Object.freeze(val) };
                    // Object.keys(val).forEach(key => selectorVal[key] = val[key]);
                    // remove any listeners that are closed or have no subscriptions
                    this.listeners = this.listeners.filter(x => !(x.observable.closed));
                    this.listeners.forEach(x => x.observable.next(x.selector(this.state)));
                    this.stateObs.forEach(obs => obs.next(this.state));
                });
            }
        };
    }
}

const oktaAuthFactory = (config) => new OktaAuth({
    responseType: ['token', 'id_token', 'code'],
    clientId: config.clientId,
    issuer: config.issuer,
    redirectUri: config.redirectUri,
    scopes: config.scopes,
    tokenManager: { storage: sessionStorage },
    services: { syncStorage: false },
    maxClockSkew: config.maxClockSkew || 900,
    pkce: true,
    restoreOriginalUri: async () => {
        // application should handle the redirect
    }
});
const oktaAuthProvider = {
    provide: OktaAuth,
    deps: [BasicAuthConfig],
    useFactory: oktaAuthFactory
};

class AdminPlanTracker {
    get lastUsed() {
        return localStorage.getItem('lastUsedAdminPlan') || '';
    }
    set lastUsed(value) {
        localStorage.setItem('lastUsedAdminPlan', value);
    }
}

const authentication = {
    /** main focus is to exchange tokens when authenticating to a new application */
    sso: (oktaAuth) => new Promise((resolve, reject) => oktaAuth.isAuthenticated().then(hasAuth => {
        if (hasAuth) {
            if (oktaAuth.options.clientId === oktaAuth.token.decode(oktaAuth.getIdToken()).payload.aud) {
                resolve(AuthChallengeStatus.clientMatches);
            }
            else {
                oktaAuth.token.renewTokens()
                    .then(res => {
                    oktaAuth.tokenManager.add('idToken', res.idToken);
                    oktaAuth.tokenManager.add('accessToken', res.accessToken);
                    resolve(AuthChallengeStatus.clientMisMatch);
                })
                    .catch(x => { console.error(x); reject(x); });
            }
        }
        else {
            oktaAuth.token.getWithoutPrompt().then(res => {
                oktaAuth.tokenManager.setTokens(res.tokens);
                resolve(AuthChallengeStatus.clientMisMatch);
                // eslint-disable-next-line no-unused-vars
            }).catch(e => resolve(AuthChallengeStatus.notAuthenticated));
        }
    })),
    /** will return the current status. if that status is not authenticated it is held until authentication has been completed */
    status: (oktaAuth) => new Promise(resolve => oktaAuth.isAuthenticated().then(hasAuth => {
        if (hasAuth) {
            if (oktaAuth.options.clientId === oktaAuth.token.decode(oktaAuth.getIdToken()).payload.aud) {
                resolve(AuthChallengeStatus.clientMatches);
            }
            else {
                resolve(AuthChallengeStatus.clientMisMatch);
            }
        }
        else {
            oktaAuth.tokenManager.on('added', () => resolve(AuthChallengeStatus.newlyAuthenticated));
        }
    })),
    signOut: (oktaAuth) => oktaAuth.signOut()
};
var AuthChallengeStatus;
(function (AuthChallengeStatus) {
    AuthChallengeStatus["clientMatches"] = "authenticated::client-match";
    AuthChallengeStatus["clientMisMatch"] = "authenticated::client-mismatch";
    AuthChallengeStatus["newlyAuthenticated"] = "newly-authenticated";
    AuthChallengeStatus["notAuthenticated"] = "not-authenticated";
})(AuthChallengeStatus || (AuthChallengeStatus = {}));

/* eslint-disable indent */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable max-len */
/* eslint-disable guard-for-in */
const unflatten = (data) => {
    const result = {};
    for (const i in data) {
        const keys = i.split('.');
        keys.reduce((r, e, j) => r[e] || (r[e] = isNaN(Number(keys[j + 1])) ? (keys.length - 1 === j ? data[i] : {}) : []), result);
    }
    return result;
};
const parseConfigSet = (value) => {
    if (typeof (value) === 'string') {
        if (value.startsWith('[]')) {
            return value.replace('[]', '').split(',');
        }
        else if (value.startsWith('JSON')) {
            const temp = value.replace('JSON', '');
            return JSON.parse(temp.substring(1, temp.length - 1));
        }
        /* if (value.startsWith('[]')) {
          throw new Error('New template files call for the json format. You should not be using the property file format anymore. Found a string starting with []');
        } else if (value.startsWith('JSON')) {
          throw new Error('New template files call for the json format. You should not be using the property file format anymore. Found a string starting with JSON');
        } */
    }
    return value;
};
const applyOverrides = (config, overrides) => {
    if (overrides) {
        const overridesCombined = Object.keys(overrides).filter(key => window.location.href.indexOf(key) !== -1).reduce((bag, key) => ({ ...bag, ...overrides[key] }), {});
        config = Object.keys(overridesCombined)
            .map(key => ({ key, value: parseConfigSet(overridesCombined[key]) }))
            .reduce((bag, kv) => ({
            ...bag,
            // if override is an object, combine objects together. only works one level down.
            [kv.key]: !isObject(kv.value) ? kv.value : (!Array.isArray(kv.value) ? { ...(bag[kv.key] || {}), ...kv.value } : [...(bag[kv.key] || []), ...kv.value])
        }), { ...config, overrides: undefined });
    }
    return config;
};
const isObject = (obj) => {
    const type = typeof obj;
    return type === 'function' || type === 'object' && !!obj;
};
const bootstrap = (config, platformBrowserDynamic, module, extraProviderFactory) => config
    .then(x => x.json().then(data => unflatten(data)))
    .then((x) => Object.keys(x).reduce((bag, key) => ({ ...bag, [key]: (parseConfigSet(x[key])) }), {}))
    .then(x => applyOverrides(x, x.overrides))
    .then(x => [x, oktaAuthFactory(x)])
    .then(([x, oktaAuth]) => oktaAuth.start().then(() => authentication.sso(oktaAuth).then(() => [x, oktaAuth])))
    .then(([x, oktaAuth]) => platformBrowserDynamic([{
        provide: BasicAuthConfig,
        useValue: x
    },
    {
        provide: OktaAuth,
        useValue: oktaAuth || ''
    },
    ...(extraProviderFactory ? extraProviderFactory(x) : [])
]).bootstrapModule(module));

const commonUtil = {
    adminPlan: new AdminPlanTracker(),
    authentication,
    bootstrap
};

/**
 * T are the pieces of authorization. which is represented by {feature1: any, feature2: any}
 * when constructing the feature component we subscribe to the section of the state defined by the key
 * we take that list of strings and turn them into { [key: keyof T]: boolean } doing this allows us to
 * *ngIf="features.xyz" or this.features.xzy the return from either tell us if the feature should show
 */
/** @dynamic */
class FeatureComponent {
    constructor(allFeatures) {
        this.allFeatures = allFeatures;
        this.featuresSub = undefined;
        this.features = undefined;
        // default all to false
        this.features = Object.keys(this.allFeatures).reduce((bag, featureKey) => ({
            ...bag,
            [featureKey]: false
        }), {});
    }
    ngOnDestroy() {
        [this.featuresSub].filter(x => !!x && !x.closed).forEach(x => x.unsubscribe());
    }
    loadFeatures(features$) {
        // this comes in as a list from the state
        this.featuresSub = features$.subscribe((value) => this.features = Object.keys(this.allFeatures).reduce((bag, featureKey) => ({
            ...bag,
            [featureKey]: value.indexOf(featureKey) !== -1
        }), {}));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: FeatureComponent, deps: "invalid", target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: FeatureComponent, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: FeatureComponent, decorators: [{
            type: Directive,
            args: [{}]
        }], ctorParameters: () => [{ type: undefined }] });

class InsertableComponent {
    constructor(zone, insertToTriggerVerticalAlignment = 'top-top', insertToTriggerHorizontalAlignment = 'right-left') {
        this.zone = zone;
        this.insertToTriggerVerticalAlignment = insertToTriggerVerticalAlignment;
        this.insertToTriggerHorizontalAlignment = insertToTriggerHorizontalAlignment;
        this._elementRef = undefined;
        this._trigger = undefined;
        this.visibility = 'hidden';
        this.top = undefined;
        this.left = undefined;
        this.viewportSize = new DOMRect(0, 0, (window.innerWidth || document.documentElement.clientWidth), (window.innerHeight || document.documentElement.clientHeight));
    }
    linkContent(elementRef) {
        this._elementRef = elementRef;
        // eslint-disable-next-line no-unused-vars
        this.contentObserver = new window.ResizeObserver((entries) => this.zone.run(() => { if (this.visibility !== 'hidden') {
            this.insert(this._trigger);
        } }));
    }
    insert(trigger) {
        this._trigger = trigger;
        const insertedRect = this._elementRef.nativeElement.getBoundingClientRect();
        const triggerRect = trigger.getBoundingClientRect();
        if (this.insertToTriggerVerticalAlignment === 'top-top') {
            this.top = triggerRect.y;
        }
        else if (this.insertToTriggerVerticalAlignment === 'top-bottom') {
            this.top = triggerRect.y - insertedRect.height;
        }
        else if (this.insertToTriggerVerticalAlignment === 'bottom-bottom') {
            this.top = triggerRect.y + triggerRect.height + 1 - insertedRect.height;
        }
        else if (this.insertToTriggerVerticalAlignment === 'bottom-top') {
            this.top = triggerRect.y + triggerRect.height + 1;
        }
        // does the y need to be adjusted
        if (insertedRect.height + triggerRect.y > this.viewportSize.height) {
            // flip the menu to the top
            if (insertedRect.height < triggerRect.y) {
                this.top = this.top - (insertedRect.height + triggerRect.height);
            }
            else {
                this.top = triggerRect.y - insertedRect.height / 2;
            }
            if (this.top < 5) {
                this.top = 5;
            }
            else if (this.top + insertedRect.height > this.viewportSize.height) {
                this.top = this.viewportSize.height - (insertedRect.height + 5);
            }
        }
        if (this.insertToTriggerHorizontalAlignment === 'right-left') {
            // if the menu would not overflow off the screen to the right
            if (insertedRect.width + triggerRect.width + triggerRect.x < this.viewportSize.width) {
                // align as expected, right side of button left side of menu
                this.left = triggerRect.width + triggerRect.x + 5;
            }
            else if (triggerRect.x < this.viewportSize.width - (insertedRect.width + 15)) {
                // if it will overflow and the it wont cover the button assign it as far right as possible
                this.left = this.viewportSize.width - (insertedRect.width + 15);
            }
            else {
                this.left = triggerRect.x - (insertedRect.width + 5);
            }
        }
        else if (this.insertToTriggerHorizontalAlignment === 'right-right') {
            // if the menu would not overflow off the screen to the left
            if (triggerRect.x + triggerRect.width - insertedRect.width > 0) {
                // align as expected, right side of button right side of menu
                this.left = triggerRect.width + triggerRect.x - insertedRect.width;
            }
            else {
                this.left = 0;
            }
        }
        else if (this.insertToTriggerHorizontalAlignment === 'left-right') {
            // if the menu would not overflow off the screen to the left
            if (triggerRect.x - insertedRect.width > 0) {
                // align as expected, left side of button right side of menu
                this.left = triggerRect.x - insertedRect.width;
            }
            else {
                this.left = 0;
            }
        }
        else if (this.insertToTriggerHorizontalAlignment === 'left-left') {
            if (insertedRect.width + triggerRect.x < this.viewportSize.width) {
                // align as expected, right side of button left side of menu
                this.left = triggerRect.x;
            }
            else if (triggerRect.x < this.viewportSize.width - (insertedRect.width + 15)) {
                // if it will overflow and the it wont cover the button assign it as far right as possible
                this.left = this.viewportSize.width - (insertedRect.width + 15);
            }
            else {
                this.left = triggerRect.x - (insertedRect.width + 5);
            }
        }
        else {
            // line the menu and the button up in the center
            this.left = (triggerRect.x + (triggerRect.width / 2)) - (insertedRect.width / 2);
            // is the button x position minus the menu width greater than 0
        } /* else if (triggerRect.x - insertedRect.width > 0) {
          if (this.insertToTriggerHorizontalAlignment === 'right-left') {
            this.left = triggerRect.x - insertedRect.width - 5;
          } else if (this.insertToTriggerHorizontalAlignment === 'right-right') {
            console.log(2);
            this.left = triggerRect.width + triggerRect.x - insertedRect.width;
          }
        } */
        this.visibility = 'visible';
        this.contentObserver.observe(this._elementRef.nativeElement);
    }
    remove() {
        this.visibility = 'hidden';
        this.contentObserver.disconnect();
    }
}

const notification = {
    status: () => new Promise(resolve => resolve(('Notification' in window) ? Notification.permission : 'unsupported')),
    resetTitle: ({ title, intervalId }) => { window.document.title = title; clearInterval(intervalId); },
    cycleTitle: (message) => ({
        title: window.document.title,
        intervalId: +setInterval(() => window.document.title = window.document.title === '...' ? message : '...', 1000)
    }),
    send: (message, cycleTitle = false) => notification.status().then(status => {
        if (status === 'granted') {
            // Check whether notification permissions have already been granted;
            // if so, create a notification
            if (window.document.visibilityState === 'hidden' && cycleTitle) {
                const data = notification.cycleTitle(message);
                window.document.addEventListener('visibilitychange', () => notification.resetTitle(data));
            }
            return { instance: new Notification(message), status };
            // …
        }
        else if (status !== 'denied') {
            // We need to ask the user for permission
            return Notification.requestPermission().then(permission => {
                // If the user accepts, let's create a notification
                if (permission === 'granted') {
                    if (window.document.visibilityState === 'hidden' && cycleTitle) {
                        const data = notification.cycleTitle(message);
                        window.document.addEventListener('visibilitychange', () => notification.resetTitle(data));
                    }
                    return { instance: new Notification(message), status };
                }
                return { status };
            });
        }
        return { status };
    })
};

const getUId = () => window.crypto.getRandomValues(new Uint32Array(1))[0].toString();

/**
 * Generated bundle index. Do not edit.
 */

export { AuthExclusionInterceptor, AuthInterceptor, BasicAuthConfig, CommonAuthGuard, CommonConfig, CommonUserService, ErrorInterceptor, ErrorMessageMap, FeatureComponent, InsertableComponent, Lux, LuxDevTools, LuxHP, RooseveltCommonConfig, commonUtil, defaultErrorMessageMap, getUId, handle401, interception, notification, oktaAuthFactory, oktaAuthProvider };
